import { useEffect, useState } from 'react';
import {
  useActivePractice,
  useAllProjectTasks,
  useCurrentUser,
  useDispatch,
} from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { useToggle } from '@/hooks/useToggle';
import { usePath } from '@/hooks/usePath';
import { useAppState } from '@/providers/AppStateProvider';
import { PAGES } from '@/constants/pages';
import OverviewCard, { OverviewTab } from '@/components/OverviewCard';
import UnisonProjectTasksList from '@/components/UnisonProjectTasksList';
import EmptyMessage from '../EmptyMessage';
import { TASK_TAB, type TaskTab } from './constants';
import { classes, fetchTasks, filterTasks, getViewAllFilters } from './utils';

export default function TasksCard() {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const currentUser = useCurrentUser();
  const activePractice = useActivePractice();
  const tasks = useAllProjectTasks();
  const { navigate } = usePath();
  const [activeTab, setActiveTab] = useState<TaskTab>(TASK_TAB.MY_TASKS);
  const [isFetchingData, toggleFetchingData] = useToggle(true);

  const isShowingAll = activeTab === TASK_TAB.ALL;

  useEffect(() => {
    if (activePractice && currentUser) fetchData();
  }, [currentUser, activePractice, activeTab]);

  const fetchData = async () => {
    try {
      toggleFetchingData(true);
      dispatch.addProjectTasks(
        await fetchTasks(activePractice!.id, currentUser!.id, isShowingAll)
      );
      toggleFetchingData(false);
    } catch (err) {
      toggleFetchingData(false);
      onApiError(err, 'Error fetching tasks', () => fetchData());
    }
  };

  const handleViewAll = () => {
    navigate(PAGES.TASKS, {
      filters: JSON.stringify(getViewAllFilters(currentUser!.id, isShowingAll)),
    });
  };

  const renderList = () => {
    if (isFetchingData || !isEmpty(filteredTasks)) {
      return (
        <UnisonProjectTasksList
          condensed
          isFetchingData={isFetchingData}
          className={classes.list}
          unisonProjectTasks={filteredTasks}
        />
      );
    }
    return <EmptyMessage>No open tasks.</EmptyMessage>;
  };

  const filteredTasks = filterTasks(tasks, currentUser!.id, isShowingAll);

  return (
    <OverviewCard
      name="tasks"
      title="Tasks"
      defaultTab={TASK_TAB.MY_TASKS}
      onTabChange={(tab) => setActiveTab(tab as TaskTab)}
      onViewAll={handleViewAll}>
      <OverviewTab name={TASK_TAB.MY_TASKS} label="My Tasks">
        {renderList()}
      </OverviewTab>
      <OverviewTab name={TASK_TAB.ALL} label="All">
        {renderList()}
      </OverviewTab>
    </OverviewCard>
  );
}
